import React, { useEffect, useMemo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';
import { Box } from '@mui/material';
import { useRouter } from 'next/router';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import PizzaBuilderContainer from '../../pizza/PizzaBuilderContainer';
import {
  clearDealToDisplay,
  userSelectionsSelector
} from '../slice/userSelections.slice';
import { CurrentStep } from '../slice/dealTypes';
import StepType from '../StepType';
import ProductMenuStep from './ProductMenuStep/ProductMenuStep';
import DealType from '@/builders/deals/DealTypes';
import { clearDeal, dealSelectors } from '@/builders/deals/slice/deal.slice';
import { RootState } from '@/rootStateTypes';
import { NATIONAL } from '@/localization/constants';
import usePizzaDataInRedux from '@/builders/deals/hooks/usePizzaDataInRedux';
import useDealExitFunction from '@/builders/deals/hooks/useDealExitFunction';
import AddToDealButton from '@/builders/deals/AddToDeal';
import AddDealToCart from '@/builders/deals/AddToDeal/AddDealToCart';
import { PIZZA } from '@/domain/constants';
import { useDealStepPricing } from './useDealStepPricing';
import formattedPrice from '@/common/formattedPrice';
import MultiRecipeSelection from '@/clientCore/menu/components/MultiRecipeSelection';
import DealPrice from '../DealDetails/components/DealPrice';
import useAddToCart from '@/cart/hooks/useAddToCart';
import Routes from '@/router/routes';
import { getDealErrorModalDetails, NOT_COMBINABLE_DEALS_TITLE } from '@/common/Modal/modalDetails';
import { openModal } from '@/localization/actions';
import { AddableCartItem } from '@/api/phdApiV2Client/request.types';
import { BuiltPizza } from '@/builders/pizza/dataTransformers/builderTypes';

interface Props {
  isEditFlow?: boolean;
  builtPizzaForSelectedRecipeId?: BuiltPizza | null;
}

interface AddToCartProps extends Props {
  price: string;
  upchargeAmount?: number;
  showQuantity?: boolean;
}

const SinglePizzaDealAddToCart = ({
  isEditFlow, price, showQuantity
}: AddToCartProps): JSX.Element => (
      <>
        <Box display="flex" alignItems="center">
          <Box marginRight={2}>
            <DealPrice price={price} />
          </Box>
          <AddDealToCart
            isEditFlow={isEditFlow}
            showQuantity={showQuantity}
          />
        </Box>
      </>
    );

const MultiPizzaDealAddToCart = ({ price, upchargeAmount }: AddToCartProps): JSX.Element => (
      <>
        <Box display="flex" alignItems="center" flexGrow={1}>
          {upchargeAmount !== 0 && (
            <Box marginRight={2}>
              <DealPrice price={price} />
            </Box>
          )}
          <AddToDealButton upchargeAmount={upchargeAmount} variant="secondary" />
        </Box>
      </>
    );

export const DealPizzaBuilder = ({ isEditFlow = false, builtPizzaForSelectedRecipeId }: Props): JSX.Element => {
  const recipes = useSelector(
    userSelectionsSelector.selectRecipesForCurrentStep
  );
  const dataLoading = usePizzaDataInRedux(builtPizzaForSelectedRecipeId);
  const dealType = useSelector(dealSelectors.selectDealType);
  const isPizza = recipes?.[0]?.type === PIZZA;
  const isLocalized = useSelector(
    (state: RootState) => state.domain.localization.localizedStore !== NATIONAL
  );
  const { basePrice, totalUpChargeAmount } = useDealStepPricing();
  const [{
    enabled: isSingleStepPizzaDealQuantityPickerEnabled, variationKey
  }] = useDecision('fr-dtg-218-quantity-picker-single-step-pizza-deal');
  const isSingleStepDealQtyPickerABTestOn = isSingleStepPizzaDealQuantityPickerEnabled && variationKey === 'control_qty_pick_pizza_deal_on';

  const addToCartButton = useMemo(() => {
    switch (dealType) {
      case DealType.MULTI_STEP:
        return (
          <MultiPizzaDealAddToCart
            price={`+${formattedPrice(totalUpChargeAmount)}`}
            upchargeAmount={totalUpChargeAmount}
          />
        );
      case DealType.SINGLE_STEP:
        return (
          <SinglePizzaDealAddToCart
            isEditFlow={isEditFlow}
            price={formattedPrice(basePrice + totalUpChargeAmount)}
            showQuantity={isSingleStepDealQtyPickerABTestOn && isPizza}
          />
        );
      default:
        return (
          <AddDealToCart
            isEditFlow={isEditFlow}
            showQuantity={isSingleStepDealQtyPickerABTestOn && isPizza}
          />
        );
    }
  }, [basePrice, dealType, isEditFlow, isPizza, isSingleStepDealQtyPickerABTestOn, totalUpChargeAmount]);

  return (
    <PizzaBuilderContainer
      recipes={recipes}
      isLocalized={isLocalized}
      loading={dataLoading}
      onExit={useDealExitFunction()}
      AddToCartButton={addToCartButton}
      isEditFlow={isEditFlow}
    />
  );
};

const SingleRecipe = (props: Props): JSX.Element => (
  <DealPizzaBuilder {...props} />
);

const DealStep = ({
  step,
  isEditFlow = false
}: {
  step?: CurrentStep | null;
  isEditFlow?: boolean;
}): JSX.Element => {
  const router = useRouter();
  const dispatch = useDispatch();
  const dealType = useSelector(dealSelectors.selectDealType);
  const deal = useSelector(dealSelectors.selectDeal);
  const recipes = useSelector(userSelectionsSelector.selectSelectedRecipeByStepId);
  const selectedRecipe = recipes(step?.id ?? '', step?.index ?? 0);
  const { addToCart, isAddingCartItem, addToCartStatus } = useAddToCart();
  const isSingleStep = dealType === DealType.SINGLE_STEP;
  const isMultiPizzaDeal = step?.type === StepType.MULTI_PIZZA_RECIPE;

  useEffect(() => {
    const handleDealAddedToCartSuccess = () => {
      router.push(Routes.UPSELL).then(() => {
        batch(() => {
          dispatch(clearDeal());
          dispatch(clearDealToDisplay());
        });
      });
    };

    const handleDealAddedToCartError = (err?: Error) => {
      const dealErrorModalDetails = getDealErrorModalDetails(NOT_COMBINABLE_DEALS_TITLE, err!.message);
      dispatch(openModal(dealErrorModalDetails));
    };

    if (isMultiPizzaDeal && isSingleStep && selectedRecipe?.isQuickAdd && addToCartStatus === QueryStatus.uninitialized && deal) {

      const dealWithQuantity: AddableCartItem = {
        ...deal,
        data: {
          ...deal.data,
          quantity: selectedRecipe.item.quantity
        }
      }
      addToCart(dealWithQuantity, handleDealAddedToCartSuccess, handleDealAddedToCartError);
    }
  }, [addToCart, deal, dispatch, isMultiPizzaDeal, isSingleStep, router, selectedRecipe, addToCartStatus]);

  switch (step?.type) {
    case StepType.SINGLE_PIZZA_RECIPE: {
      return <SingleRecipe isEditFlow={isEditFlow} />;
    }
    case StepType.MULTI_PIZZA_RECIPE: {
      return <MultiRecipeSelection isEditFlow={isEditFlow} isAddToCartLoading={isAddingCartItem} isQuickAdd={isSingleStep} />;
    }
    case StepType.MENU_RECIPE: {
      return (
        <ProductMenuStep
          stepName={step.name}
        />
      );
    }
    default: {
      return <></>;
    }
  }
};

export default DealStep;
